import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ContactCheck, mustConfirm } from "@/components/CustomerAuth";
import api, { formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { HeartHandshake, Loader2, Minus, Plus, Search } from "lucide-react";

/** Donate items to an NGO: pick products from PawBazaar shops near the NGO,
 *  pay, and the order is delivered to the NGO (never to you). The NGO's
 *  address is set by the server; you only see the NGO's name and city. */
export default function Donate() {
  const { needId } = useParams();
  const { user } = useAuth();
  // No foreign contributions (FCRA): donors need an Indian mobile on their account.
  const digits = (user?.phone || "").replace(/\D/g, "");
  const indianMobile = /^(91)?[6-9]\d{9}$/.test(digits);
  const navigate = useNavigate();
  const [need, setNeed] = useState(null);
  const [q, setQ] = useState("");
  const [products, setProducts] = useState(null);
  const [basket, setBasket] = useState({});   // offer_id -> {name, price, vendor_name, quantity}
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/community/needs/${needId}`).then(({ data }) => { setNeed(data); setQ(data.title); },
      () => setNeed(false));
  }, [needId]);

  const near = need?.deliver_near;
  const remaining = need?.quantity_remaining ?? 0;
  const product = need?.product;
  const [offers, setOffers] = useState(null);   // when the NGO asked for one exact product
  const [offerId, setOfferId] = useState(null);
  useEffect(() => {
    if (!product || !near || !need?.can_donate) return;
    api.get(`/products/${product.id}`, { params: { lat: near.lat, lng: near.lng } })
      .then(({ data }) => {
        const rows = (data.offers || []).filter((o) => (o.stock || 0) > 0);
        setOffers(rows);
        const o = rows[0];
        if (o) {
          setOfferId(o.id);
          setBasket({ [o.id]: { name: data.name, price: o.price, vendor_name: o.vendor?.shop_name, quantity: Math.min(remaining, o.stock) } });
        }
      }, () => setOffers([]));
  }, [product, near, need]); // eslint-disable-line react-hooks/exhaustive-deps
  const search = async (text) => {
    if (!near) return;
    try {
      const { data } = await api.get("/products", { params: { q: text || undefined, lat: near.lat, lng: near.lng } });
      setProducts(data.filter((p) => p.best_offer));
    } catch (e) { setProducts([]); }
  };
  useEffect(() => { if (need?.can_donate && !need.product) search(need.title); }, [need]); // eslint-disable-line react-hooks/exhaustive-deps

  const lines = useMemo(() => Object.entries(basket).filter(([, l]) => l.quantity > 0), [basket]);
  useEffect(() => {
    if (!user || !lines.length) { setPreview(null); return; }
    api.post("/cart/preview", { items: lines.map(([offer_id, l]) => ({ offer_id, quantity: l.quantity })), donation_need_id: needId })
      .then(({ data }) => setPreview(data), () => setPreview(null));
  }, [user, lines, needId]);

  const units = lines.reduce((n, [, l]) => n + l.quantity, 0);
  const add = (p, delta) => setBasket((b) => {
    const o = p.best_offer;
    const cur = b[o.id]?.quantity || 0;
    const room = remaining - units + cur;   // never more than is still needed
    return { ...b, [o.id]: { name: p.name, price: o.price, vendor_name: o.vendor_name, quantity: Math.max(0, Math.min(room, 100, cur + delta)) } };
  });
  const pickOffer = (o, qty) => {
    const q = Math.max(0, Math.min(qty, remaining, o.stock || 0));
    setOfferId(o.id);
    setBasket({ [o.id]: { name: product.name, price: o.price, vendor_name: o.vendor?.shop_name, quantity: q } });
  };

  const pay = async () => {
    const items = lines.map(([offer_id, l]) => ({ offer_id, quantity: l.quantity }));
    setBusy(true);
    let paid = false;
    try {
      const { data: cfg } = await api.get("/payments/config");
      if (cfg?.enabled) {
        if (typeof window.Razorpay !== "function") throw new Error("The payment window couldn't load. Please try again.");
        const { data: rzpOrder } = await api.post("/payments/razorpay/create-order", { items, donation_need_id: needId, currency: "INR" });
        const res = await new Promise((resolve, reject) => {
          const rzp = new window.Razorpay({
            key: rzpOrder.key_id, amount: rzpOrder.amount, currency: rzpOrder.currency, order_id: rzpOrder.razorpay_order_id,
            name: "PawBazaar", description: `Donation to ${need.ngo.name}`,
            prefill: { name: user.name || "", email: user.email || "", contact: user.phone || "" },
            theme: { color: "#ea9739" }, handler: resolve, modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
          });
          rzp.on("payment.failed", (r) => reject(new Error(r?.error?.description || "Payment failed")));
          rzp.open();
        });
        const ctx = { razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature };
        await api.post("/payments/razorpay/verify", ctx);
        paid = true;
        await api.post("/orders/checkout", ctx);
      } else if (cfg?.mock_enabled) {
        await api.post("/orders/checkout", { items, donation_need_id: needId });
      } else {
        throw new Error("Online payment is unavailable right now. Please try again later.");
      }
      toast.success(`Thank you! Your donation is on its way to ${need.ngo.name}.`);
      navigate("/account");
    } catch (err) {
      const msg = err?.message === "Payment cancelled" ? "Payment cancelled" : (formatApiErrorDetail(err.response?.data?.detail) || err.message);
      toast.error(paid ? `${msg} Your payment will be refunded if the donation isn't placed.` : msg, { duration: 9000 });
    } finally { setBusy(false); }
  };

  if (need === null) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" /></div>;
  if (need === false) return <div className="max-w-xl mx-auto p-10 text-center text-stone-600">This donation request isn't available.</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="donate-page">
      <Card className="p-6 rounded-3xl border-stone-200">
        <div className="flex items-center gap-2 text-teal text-sm font-semibold"><HeartHandshake className="h-5 w-5" /> Donate to {need.ngo.name}, {need.ngo.city}</div>
        <h1 className="font-heading text-3xl font-semibold mt-2">{need.title}</h1>
        <p className="text-sm text-stone-600 mt-1">Needed: {need.quantity_needed} {need.unit}{need.description ? ` · ${need.description}` : ""}</p>
        <div className="mt-3 max-w-md" data-testid="need-progress">
          <div className="h-2 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-teal" style={{ width: `${Math.min(100, 100 * (need.quantity_needed - remaining) / Math.max(1, need.quantity_needed))}%` }} /></div>
          <div className="text-xs text-stone-500 mt-1">{need.quantity_received || 0} received · {need.quantity_pending || 0} on the way · <b className="text-stoneWarm">{remaining} still needed</b></div>
        </div>
        <p className="text-sm text-stone-500 mt-2">You pay for the items; a PawBazaar shop near the NGO delivers them to the NGO's address.</p>
      </Card>

      {!need.can_donate ? (
        <Card className="p-6 rounded-3xl border-stone-200 text-stone-600">{need.status === "open" && remaining <= 0 ? "Other donors have already covered this. Thank you for thinking of them!" : "This NGO isn't taking donations for this right now. Thank you for thinking of them!"}</Card>
      ) : !user ? (
        <Card className="p-6 rounded-3xl border-stone-200">
          <p>Sign in to donate.</p>
          <Button className="mt-3 rounded-full" data-testid="donate-signin" onClick={() => navigate(`/login?next=${encodeURIComponent(`/donate/${needId}`)}`)}>Sign in</Button>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {product ? (
              <Card className="p-5 rounded-3xl border-stone-200 space-y-4" data-testid="donate-product">
                <div className="flex gap-4 items-center">
                  {product.image_url ? <img src={mediaUrl(product.image_url)} alt="" className="h-20 w-20 rounded-2xl object-cover" /> : <div className="h-20 w-20 rounded-2xl bg-stone-100" />}
                  <div><div className="font-heading text-lg font-semibold">{product.name}</div>
                    <div className="text-sm text-stone-500">{[product.brand, product.size_variant].filter(Boolean).join(" · ")}</div>
                    <div className="text-xs text-teal mt-1">The exact product {need.ngo.name} asked for</div></div>
                </div>
                {offers === null ? <Loader2 className="h-5 w-5 animate-spin text-stone-400" /> : !offers.length ? (
                  <p className="text-sm text-stone-500">No shop near the NGO has this in stock right now. Please check again later.</p>
                ) : (
                  <>
                    <div>
                      <div className="text-sm font-semibold mb-2">From</div>
                      <div className="space-y-2">
                        {offers.slice(0, 5).map((o) => (
                          <label key={o.id} className={`flex items-center gap-3 rounded-2xl border p-3 cursor-pointer ${offerId === o.id ? "border-teal bg-teal/5" : "border-stone-200"}`}>
                            <input type="radio" checked={offerId === o.id} onChange={() => pickOffer(o, basket[offerId]?.quantity || remaining)} />
                            <span className="flex-1 text-sm">{o.vendor?.shop_name}{o.distance_km != null ? ` · ${Number(o.distance_km).toFixed(1)} km from the NGO` : ""}</span>
                            <span className="font-semibold">₹{o.price}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    {offerId && (() => {
                      const o = offers.find((x) => x.id === offerId);
                      const qty = basket[offerId]?.quantity || 0;
                      const max = Math.min(remaining, o.stock || 0);
                      return (
                        <div>
                          <div className="text-sm font-semibold mb-2">How many will you give?</div>
                          <div className="flex flex-wrap items-center gap-3">
                            <Button size="icon" variant="outline" className="rounded-full" onClick={() => pickOffer(o, qty - 1)} disabled={qty <= 1} aria-label="Less"><Minus className="h-4 w-4" /></Button>
                            <span className="w-10 text-center text-lg font-semibold" data-testid="donate-qty">{qty}</span>
                            <Button size="icon" variant="outline" className="rounded-full" onClick={() => pickOffer(o, qty + 1)} disabled={qty >= max} aria-label="More" data-testid="donate-more"><Plus className="h-4 w-4" /></Button>
                            {max > 1 && qty !== max && <Button variant="outline" className="rounded-full" onClick={() => pickOffer(o, max)} data-testid="donate-all">All {max} still needed</Button>}
                          </div>
                          {max < remaining && <p className="text-xs text-stone-500 mt-2">This shop has {o.stock} in stock.</p>}
                          <p className="text-xs text-stone-500 mt-2">Give what you can. Every bit helps, and others can cover the rest.</p>
                        </div>
                      );
                    })()}
                  </>
                )}
              </Card>
            ) : <>
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); search(q); }}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="rounded-full" data-testid="donate-search" />
              <Button type="submit" variant="outline" className="rounded-full"><Search className="h-4 w-4" /></Button>
            </form>
            {products === null ? <Loader2 className="h-5 w-5 animate-spin text-stone-400" /> : !products.length ? (
              <p className="text-sm text-stone-500">No shops near the NGO have this. Try another search.</p>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-3">
                {products.map((p) => {
                  const qty = basket[p.best_offer.id]?.quantity || 0;
                  return (
                    <li key={p.id} className="rounded-2xl border border-stone-200 bg-white p-3 flex gap-3" data-testid={`donate-product-${p.id}`}>
                      {p.best_offer.image_url || p.image_url ? <img src={mediaUrl(p.best_offer.image_url || p.image_url)} alt="" className="h-16 w-16 rounded-xl object-cover" /> : <div className="h-16 w-16 rounded-xl bg-stone-100" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium line-clamp-2">{p.name}</div>
                        <div className="text-xs text-stone-500">₹{p.best_offer.price} · {p.best_offer.vendor_name}</div>
                        <div className="mt-2 flex items-center gap-2">
                          <Button size="icon" variant="outline" className="h-7 w-7 rounded-full" onClick={() => add(p, -1)} disabled={!qty} aria-label="Less"><Minus className="h-3 w-3" /></Button>
                          <span className="w-6 text-center text-sm">{qty}</span>
                          <Button size="icon" variant="outline" className="h-7 w-7 rounded-full" onClick={() => add(p, 1)} aria-label="More" data-testid={`donate-add-${p.id}`}><Plus className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {remaining > 0 && <p className="text-xs text-stone-500">Up to {remaining} {need.unit} are still needed.</p>}
            </>}
          </div>
          <Card className="p-5 rounded-3xl border-stone-200 h-fit space-y-3" data-testid="donate-summary">
            <h3 className="font-heading text-lg font-semibold">Your donation</h3>
            {!lines.length ? <p className="text-sm text-stone-500">Add items to donate.</p> : (
              <ul className="text-sm space-y-1">{lines.map(([id, l]) => <li key={id} className="flex justify-between"><span>{l.quantity} × {l.name}</span><span>₹{(l.price * l.quantity).toFixed(0)}</span></li>)}</ul>
            )}
            {preview && <>
              <div className="flex justify-between text-sm text-stone-600"><span>Delivery to the NGO</span><span>₹{(preview.delivery_fee || 0).toFixed(0)}</span></div>
              <div className="flex justify-between font-semibold border-t pt-2"><span>Total</span><span>₹{preview.customer_payable.toFixed(0)}</span></div>
            </>}
            {!indianMobile && (
              <p className="text-sm text-terracotta" data-testid="donate-india-only">Donations are open to donors in India only. Add your Indian mobile number in your <Link to="/account?tab=profile" className="text-teal underline">profile</Link> to donate.</p>
            )}
            <ContactCheck checkout />
            <Button disabled={busy || !lines.length || !preview || !indianMobile || mustConfirm(user)} onClick={pay} className="w-full rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90" data-testid="donate-pay">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : preview ? `Pay ₹${preview.customer_payable.toFixed(0)} and donate` : "Donate"}
            </Button>
            <p className="text-xs text-stone-500">Delivered to {need.ngo.name}. You'll see it in your orders. <Link to="/account" className="text-teal">My orders</Link></p>
            <p className="text-xs text-stone-500">Once it's delivered you can download a donation acknowledgement (PDF) from My community. Donations of goods aren't covered by 80G tax receipts.</p>
          </Card>
        </div>
      )}
    </div>
  );
}
